import React, { useState, useEffect } from 'react';
import { Leaf, AlertCircle, BarChart, Wind, Settings as Lungs, Heart, Brain, ArrowRight, Download, Share2, Info, ChevronDown, ChevronUp, HelpCircle, FileText, Zap, Truck, User, Clock, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';

interface EmissionData {
  co2PerKm: number;
  co2PerYear: number;
  fuelPerYear: number;
  costPerYear: number;
  healthRisk: 'low' | 'medium' | 'high';
  treesNeeded: number;
  electricSavings: number;
}

interface MotorcycleType {
  id: string;
  name: string;
  engineSize: string;
  co2PerKm: number;
  fuelConsumption: number;
  image: string;
}

const EmissionCalculator: React.FC = () => {
  useEffect(() => {
    document.title = 'Calculateur d\'Émissions CO₂ | Proxy Emobility';
  }, []);

  const [motorcycleType, setMotorcycleType] = useState<string>('small');
  const [kmPerDay, setKmPerDay] = useState<number>(30);
  const [daysPerWeek, setDaysPerWeek] = useState<number>(5);
  const [fuelPrice, setFuelPrice] = useState<number>(750); // FCFA per liter
  const [showResults, setShowResults] = useState<boolean>(false);
  const [emissionData, setEmissionData] = useState<EmissionData | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  const motorcycleTypes: MotorcycleType[] = [
    { 
      id: 'small', 
      name: 'Petite cylindrée (50-125cc)', 
      engineSize: '50-125cc',
      co2PerKm: 60, // g/km
      fuelConsumption: 2.5, // L/100km
      image: 'https://images.pexels.com/photos/2519374/pexels-photo-2519374.jpeg'
    },
    { 
      id: 'medium', 
      name: 'Moyenne cylindrée (125-250cc)', 
      engineSize: '125-250cc',
      co2PerKm: 80, // g/km
      fuelConsumption: 3.2, // L/100km
      image: 'https://images.pexels.com/photos/2393835/pexels-photo-2393835.jpeg'
    },
    { 
      id: 'large', 
      name: 'Grande cylindrée (250cc+)', 
      engineSize: '250cc+',
      co2PerKm: 110, // g/km
      fuelConsumption: 4.5, // L/100km
      image: 'https://images.pexels.com/photos/1413412/pexels-photo-1413412.jpeg'
    },
    { 
      id: 'tricycle', 
      name: 'Tricycle / Moto-taxi', 
      engineSize: 'Divers',
      co2PerKm: 95, // g/km
      fuelConsumption: 4.0, // L/100km
      image: 'https://images.pexels.com/photos/2611675/pexels-photo-2611675.jpeg'
    }
  ];

  const healthEffects = [
    {
      icon: <Lungs size={24} className="text-red-500" />,
      title: "Problèmes Respiratoires",
      description: "Exposition aux particules fines (PM2.5) augmentant les risques d'asthme, de bronchite et d'autres maladies respiratoires.",
      stats: "20% de risque accru de maladies respiratoires"
    },
    {
      icon: <Heart size={24} className="text-red-500" />,
      title: "Maladies Cardiovasculaires",
      description: "L'exposition prolongée à la pollution atmosphérique augmente le risque de maladies cardiaques et d'AVC.",
      stats: "15% de risque accru de maladies cardiovasculaires"
    },
    {
      icon: <Brain size={24} className="text-red-500" />,
      title: "Impact Neurologique",
      description: "Les particules fines peuvent traverser la barrière hémato-encéphalique, affectant le développement cognitif.",
      stats: "Diminution des fonctions cognitives à long terme"
    },
    {
      icon: <AlertCircle size={24} className="text-red-500" />,
      title: "Autres Effets",
      description: "Irritation des yeux, de la gorge, maux de tête et fatigue chronique liés à l'exposition aux gaz d'échappement.",
      stats: "Impacts sur la qualité de vie quotidienne"
    }
  ];

  const environmentalImpacts = [
    {
      icon: <Wind size={24} className="text-green-600" />,
      title: "Qualité de l'Air",
      description: "Les motos thermiques contribuent significativement à la pollution atmosphérique urbaine au Sénégal.",
      stats: "Jusqu'à 30% de la pollution urbaine dans certaines zones"
    },
    {
      icon: <Leaf size={24} className="text-green-600" />,
      title: "Émissions de CO₂",
      description: "Contribution au réchauffement climatique par les émissions de gaz à effet de serre.",
      stats: "Environ 2 tonnes de CO₂ par moto par an"
    }
  ];

  const calculateEmissions = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const selectedMotorcycle = motorcycleTypes.find(m => m.id === motorcycleType);
      
      if (!selectedMotorcycle) {
        toast.error('Veuillez sélectionner un type de moto');
        setIsCalculating(false);
        return;
      }
      
      // Calculate annual distance
      const kmPerYear = kmPerDay * daysPerWeek * 52;
      
      // Calculate CO2 emissions
      const co2PerYear = (selectedMotorcycle.co2PerKm * kmPerYear) / 1000; // kg CO2 per year
      
      // Calculate fuel consumption
      const fuelPerYear = (selectedMotorcycle.fuelConsumption * kmPerYear) / 100; // Liters per year
      
      // Calculate fuel cost
      const costPerYear = fuelPerYear * fuelPrice; // FCFA per year
      
      // Determine health risk based on emissions and usage
      let healthRisk: 'low' | 'medium' | 'high' = 'low';
      if (co2PerYear > 500) {
        healthRisk = 'high';
      } else if (co2PerYear > 250) {
        healthRisk = 'medium';
      }
      
      // Calculate trees needed to offset emissions (1 tree absorbs ~22kg CO2 per year)
      const treesNeeded = Math.ceil(co2PerYear / 22);
      
      // Calculate potential savings with electric motorcycle
      // Assuming electric motorcycle costs ~10 FCFA per km in electricity
      const electricCostPerYear = kmPerYear * 10;
      const electricSavings = costPerYear - electricCostPerYear;
      
      setEmissionData({
        co2PerKm: selectedMotorcycle.co2PerKm,
        co2PerYear: co2PerYear,
        fuelPerYear: fuelPerYear,
        costPerYear: costPerYear,
        healthRisk: healthRisk,
        treesNeeded: treesNeeded,
        electricSavings: electricSavings
      });
      
      setShowResults(true);
      setIsCalculating(false);
    }, 1500);
  };

  const handleDownloadReport = () => {
    if (!emissionData) return;
    
    const selectedMotorcycle = motorcycleTypes.find(m => m.id === motorcycleType);
    if (!selectedMotorcycle) return;
    
    const doc = new jsPDF();
    
    // Add header
    doc.setFontSize(20);
    doc.setTextColor(24, 153, 78); // Primary color
    doc.text('Proxy Emobility - Rapport d\'Émissions CO₂', 20, 20);
    
    // Add summary
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Résumé de votre Impact Environnemental', 20, 40);
    
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text(`Type de moto: ${selectedMotorcycle.name}`, 20, 55);
    doc.text(`Distance parcourue: ${kmPerDay} km/jour, ${daysPerWeek} jours/semaine`, 20, 65);
    doc.text(`Émissions CO₂ annuelles: ${emissionData.co2PerYear.toFixed(2)} kg`, 20, 75);
    doc.text(`Consommation de carburant: ${emissionData.fuelPerYear.toFixed(2)} litres/an`, 20, 85);
    doc.text(`Coût annuel en carburant: ${emissionData.costPerYear.toLocaleString('fr-FR')} FCFA`, 20, 95);
    doc.text(`Arbres nécessaires pour compenser: ${emissionData.treesNeeded}`, 20, 105);
    doc.text(`Économies potentielles avec une moto électrique: ${emissionData.electricSavings.toLocaleString('fr-FR')} FCFA/an`, 20, 115);
    
    // Add health impact section
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Impact sur la Santé', 20, 135);
    
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text(`Niveau de risque: ${
      emissionData.healthRisk === 'low' ? 'Faible' : 
      emissionData.healthRisk === 'medium' ? 'Moyen' : 'Élevé'
    }`, 20, 150);
    
    doc.text('Effets potentiels sur la santé:', 20, 165);
    doc.text('• Problèmes respiratoires (asthme, bronchite)', 25, 175);
    doc.text('• Risques cardiovasculaires accrus', 25, 185);
    doc.text('• Impacts neurologiques à long terme', 25, 195);
    
    // Add recommendation
    doc.setFontSize(16);
    doc.setTextColor(24, 153, 78);
    doc.text('Recommandation', 20, 215);
    
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text('Envisagez de passer à une moto électrique Proxy Emobility pour:', 20, 230);
    doc.text('• Réduire votre empreinte carbone à zéro', 25, 240);
    doc.text('• Économiser sur les coûts de carburant', 25, 250);
    doc.text('• Améliorer la qualité de l\'air et votre santé', 25, 260);
    
    // Add footer
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text('www.proxy-emobility.com', 20, 280);
    doc.text('Rapport généré le ' + new Date().toLocaleDateString('fr-FR'), 140, 280);
    
    // Save the PDF
    doc.save('rapport-emissions-co2.pdf');
    toast.success('Rapport téléchargé en PDF');
  };

  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 to-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
              Calculateur d'Émissions CO₂
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Évaluez l'impact environnemental et sanitaire de votre moto thermique, et découvrez les avantages d'une transition vers l'électrique.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Calculator Form */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Calculez Votre Impact</h2>
              
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de Moto
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {motorcycleTypes.map((type) => (
                      <div 
                        key={type.id}
                        className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all ${
                          motorcycleType === type.id 
                            ? 'border-primary-500 bg-primary-50' 
                            : 'border-gray-200 hover:border-primary-200'
                        }`}
                        onClick={() => setMotorcycleType(type.id)}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={type.image} 
                              alt={type.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{type.name}</h3>
                            <p className="text-sm text-gray-500">{type.engineSize}</p>
                          </div>
                        </div>
                        
                        {motorcycleType === type.id && (
                          <div className="absolute top-2 right-2 w-4 h-4 bg-primary-500 rounded-full"></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distance parcourue par jour (km)
                  </label>
                  <div className="space-y-2">
                    <input
                      type="range"
                      min="5"
                      max="100"
                      step="5"
                      value={kmPerDay}
                      onChange={(e) => setKmPerDay(parseInt(e.target.value))}
                      className="w-full accent-primary-500"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>5 km</span>
                      <span className="font-medium text-primary-600">{kmPerDay} km</span>
                      <span>100 km</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jours d'utilisation par semaine
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => setDaysPerWeek(day)}
                        className={`flex-1 py-2 rounded-lg transition-all ${
                          daysPerWeek === day
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix du carburant (FCFA/litre)
                  </label>
                  <input
                    type="number"
                    min="500"
                    max="1500"
                    value={fuelPrice}
                    onChange={(e) => setFuelPrice(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                <button
                  onClick={calculateEmissions}
                  disabled={isCalculating}
                  className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isCalculating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                      Calcul en cours...
                    </>
                  ) : (
                    'Calculer mon impact'
                  )}
                </button>
              </div>
            </div>
            
            {/* Results or Info */}
            {showResults && emissionData ? (
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Résultats</h2>
                
                <div className="space-y-8">
                  {/* CO2 Emissions */}
                  <div className="bg-primary-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Leaf size={24} className="text-primary-600 mr-2" />
                      Émissions de CO₂
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Par kilomètre</p>
                        <p className="text-2xl font-bold text-primary-600">{emissionData.co2PerKm} g</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Par an</p>
                        <p className="text-2xl font-bold text-primary-600">{emissionData.co2PerYear.toFixed(2)} kg</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-primary-200">
                      <p className="text-sm text-gray-600 mb-1">Arbres nécessaires pour compenser</p>
                      <div className="flex items-center">
                        <p className="text-2xl font-bold text-primary-600 mr-2">{emissionData.treesNeeded}</p>
                        <div className="flex">
                          {Array.from({ length: Math.min(emissionData.treesNeeded, 5) }).map((_, i) => (
                            <Leaf key={i} size={20} className="text-green-500 -ml-1" />
                          ))}
                          {emissionData.treesNeeded > 5 && <span className="text-sm text-gray-500 ml-1">+{emissionData.treesNeeded - 5}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Fuel Consumption & Cost */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Truck size={24} className="text-gray-700 mr-2" />
                      Consommation & Coût
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Carburant par an</p>
                        <p className="text-2xl font-bold text-gray-900">{emissionData.fuelPerYear.toFixed(2)} L</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Coût annuel</p>
                        <p className="text-2xl font-bold text-gray-900">{emissionData.costPerYear.toLocaleString('fr-FR')} FCFA</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Health Impact */}
                  <div className="bg-red-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Lungs size={24} className="text-red-500 mr-2" />
                      Impact sur la Santé
                    </h3>
                    
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Niveau de risque</p>
                      <div className="flex items-center">
                        <div className={`h-3 w-3 rounded-full mr-2 ${
                          emissionData.healthRisk === 'low' ? 'bg-green-500' :
                          emissionData.healthRisk === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <p className="font-medium text-gray-900">
                          {emissionData.healthRisk === 'low' ? 'Faible' :
                           emissionData.healthRisk === 'medium' ? 'Moyen' : 'Élevé'}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => toggleSection('health')}
                      className="flex items-center justify-between w-full px-4 py-2 bg-white rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">Voir les détails</span>
                      {expandedSection === 'health' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    {expandedSection === 'health' && (
                      <div className="mt-4 space-y-4 p-4 bg-white rounded-lg">
                        {healthEffects.map((effect, index) => (
                          <div key={index} className="flex items-start">
                            <div className="mr-3 flex-shrink-0">{effect.icon}</div>
                            <div>
                              <h4 className="font-medium text-gray-900">{effect.title}</h4>
                              <p className="text-sm text-gray-600">{effect.description}</p>
                              <p className="text-sm font-medium text-red-600 mt-1">{effect.stats}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Electric Alternative */}
                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Zap size={24} className="text-green-600 mr-2" />
                      Alternative Électrique
                    </h3>
                    
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Économies potentielles par an</p>
                      <p className="text-2xl font-bold text-green-600">{emissionData.electricSavings.toLocaleString('fr-FR')} FCFA</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/products"
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
                      >
                        Découvrir nos motos électriques
                        <ArrowRight size={18} className="ml-2" />
                      </Link>
                      <button
                        onClick={handleDownloadReport}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                      >
                        <Download size={18} className="mr-2" />
                        Télécharger le rapport
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Info size={24} className="text-primary-600 mr-2" />
                  Pourquoi Calculer Vos Émissions ?
                </h2>
                
                <div className="prose max-w-none">
                  <p className="text-gray-600 mb-6">
                    Au Sénégal, les motos thermiques représentent une source significative de pollution atmosphérique, 
                    particulièrement dans les zones urbaines densément peuplées comme Dakar. Cette pollution a des 
                    conséquences directes sur la santé publique et l'environnement.
                  </p>
                  
                  <div className="mb-6">
                    <button
                      onClick={() => toggleSection('environment')}
                      className="flex items-center justify-between w-full px-4 py-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors mb-2"
                    >
                      <div className="flex items-center">
                        <Leaf size={20} className="text-primary-600 mr-2" />
                        <span className="font-medium text-gray-900">Impact Environnemental</span>
                      </div>
                      {expandedSection === 'environment' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    {expandedSection === 'environment' && (
                      <div className="p-4 bg-white rounded-lg border border-primary-100 space-y-4">
                        {environmentalImpacts.map((impact, index) => (
                          <div key={index} className="flex items-start">
                            <div className="mr-3 flex-shrink-0">{impact.icon}</div>
                            <div>
                              <h4 className="font-medium text-gray-900">{impact.title}</h4>
                              <p className="text-sm text-gray-600">{impact.description}</p>
                              <p className="text-sm font-medium text-green-600 mt-1">{impact.stats}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-6">
                    <button
                      onClick={() => toggleSection('health-info')}
                      className="flex items-center justify-between w-full px-4 py-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors mb-2"
                    >
                      <div className="flex items-center">
                        <Lungs size={20} className="text-red-500 mr-2" />
                        <span className="font-medium text-gray-900">Impact sur la Santé</span>
                      </div>
                      {expandedSection === 'health-info' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    {expandedSection === 'health-info' && (
                      <div className="p-4 bg-white rounded-lg border border-red-100 space-y-4">
                        {healthEffects.map((effect, index) => (
                          <div key={index} className="flex items-start">
                            <div className="mr-3 flex-shrink-0">{effect.icon}</div>
                            <div>
                              <h4 className="font-medium text-gray-900">{effect.title}</h4>
                              <p className="text-sm text-gray-600">{effect.description}</p>
                              <p className="text-sm font-medium text-red-600 mt-1">{effect.stats}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-primary-50 p-6 rounded-xl">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <HelpCircle size={20} className="text-primary-600 mr-2" />
                      Comment utiliser ce calculateur ?
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-600">
                      <li>Sélectionnez le type de moto que vous utilisez</li>
                      <li>Indiquez votre kilométrage quotidien moyen</li>
                      <li>Précisez le nombre de jours d'utilisation par semaine</li>
                      <li>Ajustez le prix actuel du carburant si nécessaire</li>
                      <li>Cliquez sur "Calculer mon impact" pour voir les résultats</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Notre Méthodologie
            </h2>
            <p className="text-lg text-gray-600">
              Ce calculateur utilise des données scientifiques pour estimer l'impact environnemental et sanitaire des motos thermiques au Sénégal.
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart size={24} className="text-primary-600 mr-2" />
                  Sources de Données
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <FileText size={18} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Facteurs d'émission de CO₂ basés sur les études de l'Agence Internationale de l'Énergie (AIE)</span>
                  </li>
                  <li className="flex items-start">
                    <FileText size={18} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Données de consommation de carburant issues des spécifications des fabricants</span>
                  </li>
                  <li className="flex items-start">
                    <FileText size={18} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Impacts sanitaires basés sur les recherches de l'Organisation Mondiale de la Santé (OMS)</span>
                  </li>
                  <li className="flex items-start">
                    <FileText size={18} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Données d'absorption de CO₂ par les arbres selon la FAO</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <HelpCircle size={24} className="text-primary-600 mr-2" />
                  Limites du Calculateur
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <AlertCircle size={18} className="text-amber-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Les résultats sont des estimations basées sur des moyennes et peuvent varier selon les conditions réelles d'utilisation</span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle size={18} className="text-amber-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Les impacts sanitaires sont généralisés et ne tiennent pas compte des facteurs individuels</span>
                  </li>
                  <li className="flex items-start">
                    <AlertCircle size={18} className="text-amber-500 mr-2 mt-1 flex-shrink-0" />
                    <span>Les calculs ne prennent pas en compte le cycle de vie complet des véhicules</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary-800 to-primary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
              Passez à l'Électrique
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Réduisez votre empreinte carbone à zéro, économisez sur les coûts de carburant et contribuez à un air plus sain pour tous.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/products" 
                className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-900 font-medium rounded-xl transition-colors shadow-lg hover:shadow-xl transform hover:translate-y-[-2px]"
              >
                Découvrir nos motos électriques
                <ArrowRight size={20} className="ml-2 inline-block" />
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30 transform hover:translate-y-[-2px]"
              >
                Demander un essai gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmissionCalculator;